import {
  DEFAULT_TEMPLATES,
  type EmailTemplate,
  type TemplateCategory,
} from "./email-templates.ts";

const STORAGE_KEY_TEMPLATES = "cc_admin_email_templates";
const STORAGE_KEY_SENT = "cc_admin_sent_emails";

export interface SentEmailRecord {
  id: string;
  recipientEmail: string;
  recipientName: string | null;
  senderEmail?: string;
  subject: string;
  templateId: string;
  templateName: string;
  sentAt: string;
  status: "sent" | "failed";
  error?: string | null;
}

/**
 * Retrieve all custom user-created templates from localStorage.
 */
export function getCustomTemplates(): EmailTemplate[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TEMPLATES);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as EmailTemplate[];
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn("Failed to parse custom templates from localStorage:", err);
    return [];
  }
}

/**
 * Retrieve all available templates (Default non-deletable templates first, followed by Custom templates).
 */
export function getAllTemplates(): EmailTemplate[] {
  const custom = getCustomTemplates();
  return [...DEFAULT_TEMPLATES, ...custom];
}

/**
 * Retrieve a template by its ID (searches both default and custom).
 */
export function getTemplateById(id: string): EmailTemplate | undefined {
  return getAllTemplates().find((t) => t.id === id);
}

/**
 * Save a custom template (create new or update existing).
 * Throws if attempting to overwrite a default template ID.
 */
export function saveCustomTemplate(template: Omit<EmailTemplate, "isDefault"> & { isDefault?: false }): EmailTemplate {
  if (DEFAULT_TEMPLATES.some((d) => d.id === template.id)) {
    throw new Error("Cannot overwrite a system default template.");
  }

  const custom = getCustomTemplates();
  const now = new Date().toISOString();
  const id = template.id || `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

  const finalTemplate: EmailTemplate = {
    ...template,
    id,
    isDefault: false,
    updatedAt: now,
    createdAt: template.createdAt || now,
  };

  const existingIdx = custom.findIndex((t) => t.id === id);
  if (existingIdx >= 0) {
    custom[existingIdx] = finalTemplate;
  } else {
    custom.unshift(finalTemplate);
  }

  localStorage.setItem(STORAGE_KEY_TEMPLATES, JSON.stringify(custom));
  return finalTemplate;
}

/**
 * Copy an existing template (default or custom) into a new custom template ready for editing.
 */
export function duplicateTemplate(sourceTemplate: EmailTemplate, customName?: string): EmailTemplate {
  const newName = customName || `${sourceTemplate.name} (Copy)`;
  return saveCustomTemplate({
    id: `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    name: newName,
    category: (sourceTemplate.category === "custom" ? "custom" : sourceTemplate.category) as TemplateCategory,
    subject: sourceTemplate.subject,
    html: sourceTemplate.html,
    text: sourceTemplate.text,
    description: `Copy of ${sourceTemplate.name}`,
  });
}

/**
 * Delete a custom template.
 * Throws if attempting to delete a default template.
 */
export function deleteCustomTemplate(id: string): boolean {
  if (DEFAULT_TEMPLATES.some((d) => d.id === id)) {
    throw new Error("System default templates cannot be deleted.");
  }

  const custom = getCustomTemplates();
  const filtered = custom.filter((t) => t.id !== id);
  if (filtered.length === custom.length) return false;

  localStorage.setItem(STORAGE_KEY_TEMPLATES, JSON.stringify(filtered));
  return true;
}

/**
 * Retrieve sent emails history from localStorage (most recent first).
 */
export function getSentHistory(): SentEmailRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SENT);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SentEmailRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn("Failed to parse sent history from localStorage:", err);
    return [];
  }
}

/**
 * Record sent email entries to local history log.
 */
export function recordSentEmails(records: Omit<SentEmailRecord, "id" | "sentAt">[]): void {
  const history = getSentHistory();
  const now = new Date().toISOString();

  const newRecords: SentEmailRecord[] = records.map((r, i) => ({
    ...r,
    id: `sent_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`,
    sentAt: now,
  }));

  const updated = [...newRecords, ...history].slice(0, 500); // keep last 500 entries
  try {
    localStorage.setItem(STORAGE_KEY_SENT, JSON.stringify(updated));
  } catch (err) {
    console.warn("Failed to save sent history:", err);
  }
}

/**
 * Clear sent history log.
 */
export function clearSentHistory(): void {
  localStorage.removeItem(STORAGE_KEY_SENT);
}
